import { Injectable, Inject } from "@angular/core";
import { Observable, forkJoin, of, Subject } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { catchError } from "rxjs/operators";
import jstz, { TimeZone } from "jstz";
import * as dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone.js";
dayjs.extend(timezone);
import io from "socket.io-client/dist/socket.io.slim.js";
import { Dictionary } from "highcharts";
import { DOCUMENT } from "@angular/common";
import { AppService } from "../app.service";
import { Store } from "@ngrx/store";
import { AppState } from "../store/state/app.state";
import { SetNotificationSettings } from "../store/actions/global-header.actions";
import {
  GlobalSearchResponse,
  GlobalSearchErrorResponse,
} from "./global-header.model";
import { RecentCompaniesDataResponse } from "../store/models/global-header.model";

@Injectable({
  providedIn: "root",
})
export class GlobalHeaderDataService {
  apiEP: string = window.apiUrl;
  user_timezone: TimeZone;
  legacyDomain = `${window.caUrl}/app`;
  private notificationShowSource = new Subject<Dictionary<any>>();

  notificationShow$ = this.notificationShowSource.asObservable();
  eventListHeaderMap = {
    CompanyAddedToVACoverageList: "",
    ConsensusRecalculated: "Consensus Recalculated",
    FilingsCompany: "Filing Added",
    ExcelExportReadyToBeDownloaded: "Export to Excel",
    ResearchBulkDownload: "Bulk download to Zip File",
  };

  constructor(
    private http: HttpClient,
    private appService: AppService,
    private store: Store<AppState>,
    @Inject(DOCUMENT) private _document: HTMLDocument,
  ) {
    this.user_timezone = jstz.determine();
  }

  // getGlobalSearchData(currentInput: string): Observable<any> {
  //   return this.http.get(
  //     `${this.apiEP}/api/n_tiksch?q=${encodeURIComponent(currentInput)}`
  //   );
  // }
  getGlobalSearchData(
    currentInput: string,
  ): Observable<GlobalSearchResponse | GlobalSearchErrorResponse> {
    return this.http.get<GlobalSearchResponse>(
      `${this.apiEP}/api/n_tiksch?q=${encodeURIComponent(currentInput)}`,
    );
  }

  getAddinUserSettings(): Observable<any> {
    return this.http.get(`${this.apiEP}/api/get_addin_user_settings`);
  }

  getUserCompId(): Observable<any> {
    return this.http.get(`${this.apiEP}/comps/prepare_cc_default`);
  }

  saveAddinUserSettings(
    tickerType: string,
    currencyCompatible: string,
  ): Observable<any> {
    const dataToPass = {
      ticker: "",
      jsonData: {
        ticker_type: tickerType,
        currency_compatible: currencyCompatible,
      },
    };
    return this.http.post(
      `${this.apiEP}/api/update_addin_user_settings`,
      dataToPass,
    );
  }

  getIsActive(): Observable<any> {
    const de = this.http
      .get(`${this.apiEP}/isactive?app=de`)
      .pipe(catchError((error) => of(error)));
    const oa = this.http
      .get(`${this.apiEP}/isactive?app=oa`)
      .pipe(catchError((error) => of(error)));

    return forkJoin([de, oa]);
  }

  getIsOneAccessActive(): Observable<any> {
    return this.http
      .get(`${this.apiEP}/isactive?app=oa`)
      .pipe(catchError((error) => of(error)));
  }

  getIsDEActive(): Observable<any> {
    return this.http
      .get(`${this.apiEP}/isactive?app=de`)
      .pipe(catchError((error) => of(error)));
  }

  getImitatedInfo(): Observable<any> {
    return this.http.get(`${this.apiEP}/imitate/isImitated`);
  }

  stopImitation(): Observable<any> {
    return this.http.get(`${this.apiEP}/imitate/removeImitate`);
  }

  getReleaseVersion(): Observable<any> {
    return this.http.get(`${this.apiEP}/api/get_release_version`);
  }

  getActivityNotificationData(type: string): Observable<any> {
    const userTimezone = dayjs.tz.guess();
    const oneDayAgoTimestamp = new Date(
      new Date().getTime() - 24 * 60 * 60 * 1000,
    ).toISOString();
    if (type === "Notifications") {
      return this.http.get(
        `${
          this.apiEP
        }/app/alerts/get_notifications/?timezone=${encodeURIComponent(
          userTimezone,
        )}&alerts_only=1&include_excel=1&eventPublishTime__gt=${oneDayAgoTimestamp}`,
      );
    }

    // For exports
    return this.http.get(
      `${
        this.apiEP
      }/app/alerts/get_excel_notifications/?timezone=${encodeURIComponent(
        userTimezone,
      )}`,
    );
  }

  retryExcelDownload(ticker: string, extras: any): Observable<any> {
    const parsedExtra = JSON.parse(extras);
    return this.http.get(
      `${this.apiEP}/api/prepare_exporttoexcel_object/?&ticker=${ticker}&trackerid=${parsedExtra.trackerId}`,
    );
  }

  getDownloadCount(host: string, userId: number): Observable<any> {
    return this.http.get(`${host}/get_redis_count?user=${userId}`);
  }
  resetDownloadCount(host: string, userId: number): Observable<any> {
    return this.http.get(`${host}/reset_redis_count?user=${userId}`, {
      responseType: "text",
    });
  }

  showNotification(params) {
    this.notificationShowSource.next(params);
  }

  toastNotification = (userId, host) => {
    const options = {
      reconnectionAttempts: 5,
      reconnection: true,
    };

    // exposing socket on window, so that resource-download or any other component can check
    //  if it is connected and accordingly do some operation
    window.socket = io.connect(host, options);
    window.socket.once("connect", function () {
      window.socket.emit("join", userId);
      const socketConnected = new CustomEvent("socketConnected", {
        detail: { closed: true },
      });
      window.dispatchEvent(socketConnected);
    });

    window.socket.on("notification", (data) => {
      data = JSON.parse(data);
      const alertObject = data.text;
      const toastText = `${alertObject.actor.actor_full_text} ${alertObject.verb} ${alertObject.object.object_full_text}`;
      let exportType = "";
      let extrasJson = null;
      let excelTrackerId = null;
      let headerInfo = null;
      // let fileName = null;
      let tickerName = null;
      switch (alertObject.event_type) {
        case "ExcelExportReadyToBeDownloaded":
          extrasJson = JSON.parse(alertObject.extras);
          excelTrackerId = extrasJson.trackerId;
          // fileName = extrasJson.filename;
          exportType = extrasJson.export_type;
          tickerName = alertObject.actor.actor_text;

          if (
            exportType === "surprise_export" ||
            exportType === "revision_export"
          ) {
            if (extrasJson.status.toLowerCase() === "success") {
              //success notification
              const msg =
                exportType === "surprise_export"
                  ? "Surprise Analysis exported."
                  : "Revisions Analysis exported.";
              //         toastr.success(msg, eventListHeaderMap['ExcelExportReadyToBeDownloaded'], {
              //             onclick: function () {
              //                 excel_notification_download(excelTrackerId, fileName, tickerName, exportType);
              //             }
              //         });
              this.notificationShowSource.next({
                type: "success",
                title:
                  this.eventListHeaderMap["ExcelExportReadyToBeDownloaded"],
                msg: msg,
                tickerName: tickerName,
                excelTrackerId: excelTrackerId,
                exportType: exportType,
                onClick: (event, data) => {
                  const extraParams = {
                    event: event,
                    tickerName: data.tickerName,
                    excelTrackerId: data.excelTrackerId,
                    actionSource: "TOASTR",
                  };
                  this.appService.excelDownload(data.exportType, extraParams);
                },
              });
              this.store.dispatch(
                new SetNotificationSettings({ fetchNewData: true }),
              );
            } else if (extrasJson.status.toLowerCase() === "new") {
              //         toastr.success(toastText, eventListHeaderMap['ExcelExportReadyToBeDownloaded'], {onclick: null});
              this.notificationShowSource.next({
                type: "success",
                title:
                  this.eventListHeaderMap["ExcelExportReadyToBeDownloaded"],
                msg: toastText,
                onClick: () => {
                  null;
                },
              });
            } else if (extrasJson.status.toLowerCase() === "failure") {
              //error Notification
              const addedText = `${toastText}<br>Please reinitiate the export after some time`;
              //         toastr.error(addedText, eventListHeaderMap['ExcelExportReadyToBeDownloaded'], {
              //             onclick: null
              //         })
              this.notificationShowSource.next({
                type: "error",
                title:
                  this.eventListHeaderMap["ExcelExportReadyToBeDownloaded"],
                msg: addedText,
                onClick: () => {
                  null;
                },
              });
              this.store.dispatch(
                new SetNotificationSettings({ fetchNewData: true }),
              );
            }
          } else if (extrasJson.status.toLowerCase() === "success") {
            let toastText = "";
            if (exportType !== "research_download") {
              toastText = `${alertObject.actor.actor_full_text}<br>${alertObject.verb} ${alertObject.object.object_full_text}`;
            } else {
              toastText = `Your research zip file is ready to download`;
            }
            // this.excelNotificationDownload(excelTrackerId, tickerName, exportType);
            this.notificationShowSource.next({
              type: "success",
              title:
                exportType === "research_download"
                  ? this.eventListHeaderMap["ResearchBulkDownload"]
                  : exportType === "biopharma_full_export"
                    ? "BioPharma Drugs Export"
                    : this.eventListHeaderMap["ExcelExportReadyToBeDownloaded"],
              msg: toastText,
              tickerName: tickerName,
              excelTrackerId: excelTrackerId,
              exportType: exportType,
              onClick: (event, data) => {
                const extraParams = {
                  event: event,
                  tickerName: data.tickerName,
                  excelTrackerId: data.excelTrackerId,
                  actionSource: "TOASTR",
                };
                this.appService.excelDownload(data.exportType, extraParams);
              },
            });
            this.store.dispatch(
              new SetNotificationSettings({ fetchNewData: true }),
            );
          } else if (extrasJson.status.toLowerCase() === "new") {
            let toastText = "";
            if (exportType !== "research_download") {
              toastText = `${alertObject.actor.actor_full_text}<br>${alertObject.verb} ${alertObject.object.object_full_text}`;
            } else {
              toastText = `This download will be available in the Export Folder once complete.`;
            }
            this.notificationShowSource.next({
              type: "success",
              title:
                exportType === "research_download"
                  ? this.eventListHeaderMap["ResearchBulkDownload"]
                  : exportType === "biopharma_full_export"
                    ? "BioPharma Drugs Export"
                    : this.eventListHeaderMap["ExcelExportReadyToBeDownloaded"],
              msg: toastText,
              onClick: () => {
                null;
              },
            });
          } else if (extrasJson.status.toLowerCase() === "failure") {
            //TO-DO: There is no failure implementation for research_download yet
            if (extrasJson.retryflag === true) {
              const retryText = `${toastText}<br>Click here to retry`;
              this.notificationShowSource.next({
                type: "error",
                title:
                  exportType === "biopharma_full_export"
                    ? "BioPharma Drugs Export"
                    : this.eventListHeaderMap["ExcelExportReadyToBeDownloaded"],
                msg: retryText,
                alertObject: alertObject,
                onClick: (event, data) => {
                  this.retryExcelDownload(
                    data.alertObject.actor.actor_text,
                    data.alertObject.extras,
                  ).subscribe();
                },
              });
            } else if (extrasJson.retryflag === false) {
              const addedText = `${toastText}<br>Please reinitiate the export after some time`;
              //         toastr.error(addedText, eventListHeaderMap['ExcelExportReadyToBeDownloaded'], {
              //             onclick: null
              //         })
              this.notificationShowSource.next({
                type: "error",
                title:
                  exportType === "research_download"
                    ? this.eventListHeaderMap["ResearchBulkDownload"]
                    : exportType === "biopharma_full_export"
                      ? "BioPharma Drugs Export"
                      : this.eventListHeaderMap[
                          "ExcelExportReadyToBeDownloaded"
                        ],
                msg: addedText,
                onClick: () => {
                  null;
                },
              });
            }
            this.store.dispatch(
              new SetNotificationSettings({ fetchNewData: true }),
            );
          }

          break;
        default:
          headerInfo =
            alertObject.event_type in this.eventListHeaderMap
              ? this.eventListHeaderMap[alertObject.event_type]
              : "";
          this.notificationShowSource.next({
            type: "info",
            title: headerInfo,
            msg: toastText,
            alertObject: alertObject,
            onClick: (event, data) => {
              window.location.href = `/company/${data.alertObject.actor.actor_text}`;
            },
          });

          break;
      }
    });
  };

  getRecentlyVisitedCompanies(): Observable<RecentCompaniesDataResponse> {
    const params = { MAX_COMPANY_VISITED_COUNT: "48", QUERY_LIMIT: "500" };
    return this.http.get<RecentCompaniesDataResponse>(
      `${this.apiEP}/watcher/get_recently_visited_companies/`,
      { params },
    );
  }

  getGlobalMenuData() {
    return this.http.get(`${this.apiEP}/api/hamburger_header_menu/`);
  }

  getUserSettings() {
    return this.http.get(`${this.apiEP}/api/get_user_settings /`);
  }

  saveUserSettings(dataToSend) {
    return this.http.post(`${this.apiEP}/api/save_user_settings`, dataToSend);
  }
}
